/**
 * P11 知识库问答交互 — 从 legacy-app.js 拆出。
 */
import { escapeHtml } from '../util/html.js';

/** @param {import('./portal-ui-deps.js').PortalUiDeps} deps */
export function initKnowledgeUi(deps) {
  const { requestRuntime, showRuntimeError, setRuntimeProgress } = deps;

  let latestKnowledgeBrowse = null;
  let selectedKnowledgeTag = '';
  let knowledgeBrowseLoaded = false;
  let knowledgeAskInFlight = false;

  function ensureKnowledgeBrowseLoaded(force) {
    if (knowledgeBrowseLoaded && !force) return;
    void loadKnowledgeBrowse();
  }

  async function loadKnowledgeBrowse() {
    const meta = document.getElementById('kb-nav-meta');
    const badge = document.getElementById('kb-online-badge');
    const query = document.getElementById('kb-nav-search')?.value?.trim() || '';
    try {
      const params = new URLSearchParams();
      if (query) params.set('query', query);
      if (selectedKnowledgeTag) params.set('tag', selectedKnowledgeTag);
      const qs = params.toString();
      const result = await requestRuntime('/api/business/knowledge' + (qs ? `?${qs}` : ''));
      latestKnowledgeBrowse = result;
      knowledgeBrowseLoaded = true;
      if (badge) {
        badge.textContent = '已连接';
        badge.style.color = '#16A34A';
      }
      renderKnowledgeBrowse(result);
      document.getElementById('stage-result-p11').textContent =
        `知识库已加载 · ${result.total || 0} 条` +
        (selectedKnowledgeTag ? ` · 标签 ${selectedKnowledgeTag}` : '') +
        (query ? ` · 筛选「${query}」` : '');
    } catch (error) {
      knowledgeBrowseLoaded = false;
      if (badge) {
        badge.textContent = '离线';
        badge.style.color = '#EF4444';
      }
      if (meta) meta.textContent = `加载失败：${error.message}`;
      document.getElementById('stage-result-p11').textContent = `知识库加载失败：${error.message}`;
      showRuntimeError(error.message);
    }
  }

  function renderKnowledgeBrowse(result) {
    const tagList = document.getElementById('kb-tag-list');
    const itemList = document.getElementById('kb-item-list');
    const meta = document.getElementById('kb-nav-meta');
    const followups = document.getElementById('kb-followups');
    const tags = Array.isArray(result.tagStats) ? result.tagStats : [];
    const items = Array.isArray(result.items) ? result.items : [];

    tagList.innerHTML = [
      `<button type="button" data-kb-tag="" style="border:1px solid ${selectedKnowledgeTag ? '#374151' : '#C5A55A'};background:${selectedKnowledgeTag ? '#111827' : '#C5A55A22'};color:${selectedKnowledgeTag ? '#D1D5DB' : '#C5A55A'};border-radius:12px;padding:3px 8px;font-size:10px;cursor:pointer;">全部</button>`,
      ...tags.slice(0, 12).map((row) => {
        const active = selectedKnowledgeTag === row.tag;
        return `<button type="button" data-kb-tag="${escapeHtml(row.tag)}" style="border:1px solid ${active ? '#C5A55A' : '#374151'};background:${active ? '#C5A55A22' : '#111827'};color:${active ? '#C5A55A' : '#D1D5DB'};border-radius:12px;padding:3px 8px;font-size:10px;cursor:pointer;">${escapeHtml(row.tag)} ${row.count}</button>`;
      })
    ].join('');

    itemList.innerHTML = items.length
      ? items.map((item) => `
        <button type="button" data-kb-ask-title="${escapeHtml(item.title)}"
          style="text-align:left;width:100%;cursor:pointer;border:1px solid #374151;border-radius:5px;padding:8px;background:#111827;color:#E5E7EB;">
          <div style="font-size:12px;font-weight:600;margin-bottom:4px;">${escapeHtml(item.title)}</div>
          <div style="font-size:10px;color:#9CA3AF;line-height:1.4;">${escapeHtml(item.excerpt || '')}</div>
          <div style="font-size:10px;color:#6B7280;margin-top:4px;">${escapeHtml((item.tags || []).join(' / '))}</div>
        </button>
      `).join('')
      : '<div style="font-size:11px;color:#9CA3AF;padding:8px 0;">无匹配知识条目</div>';

    meta.textContent = `共 ${result.total || 0} 条 · 标签 ${tags.length} 个`;

    const suggestions = Array.isArray(result.suggestedQuestions) ? result.suggestedQuestions : [];
    if (followups && !followups.dataset.locked) {
      followups.innerHTML = suggestions.slice(0, 4).map((q) =>
        `<button type="button" data-kb-followup="${escapeHtml(q)}" style="background:#F3F4F6;padding:5px 14px;border-radius:15px;font-size:11px;cursor:pointer;border:1px solid #E5E7EB;">${escapeHtml(q)}</button>`
      ).join('');
    }
  }

  function appendKbChatBubble(role, html) {
    const log = document.getElementById('kb-chat-log');
    if (!log) return;
    const emptyHint = log.querySelector('[data-kb-empty]');
    if (emptyHint) emptyHint.remove();
    if (log.children.length === 1 && log.querySelector('div[style*="text-align:center"]') && !log.querySelector('[data-kb-msg]')) {
      log.innerHTML = '';
    }
    const wrap = document.createElement('div');
    wrap.setAttribute('data-kb-msg', '1');
    wrap.style.cssText = role === 'user'
      ? 'display:flex;justify-content:flex-end;margin-bottom:12px;'
      : 'display:flex;justify-content:flex-start;margin-bottom:12px;';
    wrap.innerHTML = role === 'user'
      ? `<div style="background:#0B1426;color:#fff;padding:10px 14px;border-radius:12px 12px 2px 12px;max-width:78%;font-size:12px;line-height:1.6;">${html}</div>`
      : `<div style="background:#F5F1EB;border:1px solid #E5E7EB;padding:12px 14px;border-radius:12px 12px 12px 2px;max-width:88%;font-size:12px;line-height:1.6;">${html}</div>`;
    log.appendChild(wrap);
    log.scrollTop = log.scrollHeight;
  }

  function renderKbFollowups(questions) {
    const followups = document.getElementById('kb-followups');
    if (!followups) return;
    followups.dataset.locked = '1';
    const list = Array.isArray(questions) ? questions : [];
    followups.innerHTML = list.slice(0, 4).map((q) =>
      `<button type="button" data-kb-followup="${escapeHtml(q)}" style="background:#F3F4F6;padding:5px 14px;border-radius:15px;font-size:11px;cursor:pointer;border:1px solid #E5E7EB;">${escapeHtml(q)}</button>`
    ).join('');
  }

  async function askKnowledgeQuestion(question) {
    const q = String(question || '').trim();
    if (!q) {
      showRuntimeError('请输入要提问的问题。');
      return;
    }
    if (knowledgeAskInFlight) return;
    knowledgeAskInFlight = true;
    const askBtn = document.getElementById('kb-ask-btn');
    const originalText = askBtn?.textContent || '提问';
    if (askBtn) {
      askBtn.disabled = true;
      askBtn.textContent = '检索中...';
    }
    appendKbChatBubble('user', escapeHtml(q));
    setRuntimeProgress(35);
    try {
      const result = await requestRuntime('/api/business/knowledge/ask', {
        method: 'POST',
        body: JSON.stringify({
          question: q,
          tag: selectedKnowledgeTag || undefined,
          topK: 5
        })
      });
      const citations = Array.isArray(result.citations) ? result.citations : [];
      const citationHtml = citations.length
        ? `<div style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap;">${citations.map((c) =>
            `<span style="background:#F3F4F6;padding:2px 8px;border-radius:10px;font-size:10px;color:#6B7280;">${escapeHtml(c.title || c.knowledgeId)}</span>`
          ).join('')}</div>`
        : '';
      const modeLabel = result.answerMode === 'no_match' ? '无命中' : '有据回答';
      appendKbChatBubble('assistant', `
      <div style="font-size:10px;color:#9CA3AF;margin-bottom:6px;">${escapeHtml(modeLabel)} · taskId ${escapeHtml(result.taskId || '')}</div>
      <div style="white-space:pre-wrap;">${escapeHtml(result.answer || '')}</div>
      ${citationHtml}
      <div style="margin-top:8px;font-size:10px;color:#9CA3AF;">${escapeHtml(result.disclaimer || '')}</div>
    `);
      renderKbFollowups(result.followUps);
      document.getElementById('stage-result-p11').innerHTML =
        `<strong>问答完成</strong> · ${escapeHtml(modeLabel)} · 引用 ${citations.length} 条 · <code style="font-size:10px;">${escapeHtml(result.taskId || '')}</code>`;
      setRuntimeProgress(100);
    } catch (error) {
      appendKbChatBubble('assistant', `提问失败：${escapeHtml(error.message)}`);
      document.getElementById('stage-result-p11').textContent = `问答失败：${error.message}`;
      showRuntimeError(error.message);
      setRuntimeProgress(0);
    } finally {
      knowledgeAskInFlight = false;
      if (askBtn) {
        askBtn.disabled = false;
        askBtn.textContent = originalText;
      }
    }
  }

  document.getElementById('kb-refresh-btn')?.addEventListener('click', function() {
    void loadKnowledgeBrowse();
  });

  let kbSearchTimer = null;
  document.getElementById('kb-nav-search')?.addEventListener('input', function() {
    clearTimeout(kbSearchTimer);
    kbSearchTimer = setTimeout(() => { void loadKnowledgeBrowse(); }, 280);
  });

  document.getElementById('kb-tag-list')?.addEventListener('click', function(event) {
    const button = event.target.closest('[data-kb-tag]');
    if (!button) return;
    selectedKnowledgeTag = button.getAttribute('data-kb-tag') || '';
    void loadKnowledgeBrowse();
  });

  document.getElementById('kb-item-list')?.addEventListener('click', function(event) {
    const button = event.target.closest('[data-kb-ask-title]');
    if (!button) return;
    const title = button.getAttribute('data-kb-ask-title') || '';
    if (!title) return;
    document.getElementById('kb-question-input').value = `请说明：${title}`;
    void askKnowledgeQuestion(`请说明：${title}`);
  });

  document.getElementById('kb-followups')?.addEventListener('click', function(event) {
    const button = event.target.closest('[data-kb-followup]');
    if (!button) return;
    const q = button.getAttribute('data-kb-followup') || '';
    if (!q) return;
    document.getElementById('kb-question-input').value = q;
    void askKnowledgeQuestion(q);
  });

  document.getElementById('kb-ask-btn')?.addEventListener('click', function() {
    const q = document.getElementById('kb-question-input')?.value || '';
    void askKnowledgeQuestion(q);
  });

  document.getElementById('kb-question-input')?.addEventListener('keydown', function(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void askKnowledgeQuestion(this.value);
    }
  });

  return { ensureLoaded: ensureKnowledgeBrowseLoaded };
}
